import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {
  FormattedProperty,
  getFormattedPropertyName,
  getRawPropertyName,
  getRawPropertyValue,
  ValidPropertyValue,
} from 'common/util/format-property';
import {FormattedPropertyLayout} from 'common/widgets/profile-activity/formatted-property/template';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {template} from './template';
import './index.styl';

interface ProfileEditablePropertyState {
  isEditing: boolean;
  editValue: string;
  isConfirmingDelete: boolean;
}

interface ProfileEditablePropertyAttrs {
  property: FormattedProperty;
  'match-parts'?: {name: string[]; value: string[]};
  'sidebar-mode'?: boolean;
  loading: boolean;
  'can-write-profile': boolean;
}

export interface PropertyModificationEvent {
  propertyName: string;
  propertyValue: ValidPropertyValue;
}

// TODO(sam) merge this component with `mp-settings-li` to find a common ground for editable properties
export class ProfileEditableProperty extends Component<ProfileEditablePropertyState, ProfileEditablePropertyAttrs> {
  static get attrsSchema(): AttrsSchema<ProfileEditablePropertyAttrs> {
    return {
      ...super.attrsSchema,
      loading: `boolean`,
      'sidebar-mode': `boolean`,
      property: `json`,
      'match-parts': `json`,
      'can-write-profile': `boolean`,
    };
  }

  get config(): ConfigOptions<ProfileEditablePropertyState> {
    return {
      defaultState: {
        isEditing: false,
        editValue: ``,
        isConfirmingDelete: false,
      },
      template,
      helpers: {
        FormattedPropertyLayout,
        getFormattedPropertyName,
        getRawPropertyValue,
        inputInserted: (vnode): void => {
          vnode.elm.focus();
        },
        handleEditProperty: (): void => {
          this.update({
            isEditing: true,
            editValue: String(getRawPropertyValue(this.attr(`property`))),
          });
        },
        handleUpdatePropertyValue: (ev): void => {
          this.update({
            editValue: ev.target.value,
          });
        },
        handleCancelPropertyEdit: (): void => {
          this.update({
            isEditing: false,
          });
        },
        handleSavePropertyValue: (): void => {
          if (String(getRawPropertyValue(this.attr(`property`))) !== this.state.editValue) {
            this.trackEvent(TrackedEvents.UpdateProperty, {
              'Property Name': getRawPropertyName(this.attr(`property`)),
              'Property Value': this.state.editValue,
              'Old Property Value': getRawPropertyValue(this.attr(`property`)),
            });

            this.dispatchPropertyModify(`update`, getRawPropertyName(this.attr(`property`)), this.state.editValue);
          } else {
            // We need to throw this update call in the else branch of the conditional because
            // our `loading` attribute and attributeChanged callback will handle the edit state for us if it has changed
            this.update({
              isEditing: false,
            });
          }
        },
        handleDeleteButton: (ev): void => {
          ev.stopPropagation(); // needed since we have the onclick covering the whole container

          this.update({
            isConfirmingDelete: true,
          });
        },
        handleModalButton: (ev): void => {
          ev.stopPropagation();
          const {
            detail: {action},
          } = ev;
          if (action === `confirm`) {
            this.trackEvent(TrackedEvents.DeleteUserProperty, {
              'Property Name': getRawPropertyName(this.attr(`property`)),
              'Property Value': getRawPropertyValue(this.attr(`property`)),
            });

            this.dispatchPropertyModify(
              `delete`,
              getRawPropertyName(this.attr(`property`)),
              getRawPropertyValue(this.attr(`property`)),
            );
          }

          this.update({
            isConfirmingDelete: false,
          });
        },
      },
    };
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    super.attributeChangedCallback(name, oldValue, newValue);

    // Only update editing to be false once we stop loading, i.e. when we got from loading=true to loading=false
    if (name === `loading` && !this.hasAttribute(`loading`)) {
      this.update({
        isEditing: false,
      });
    }
  }

  dispatchPropertyModify(
    modificationType: `update` | `delete`,
    propertyName: string,
    propertyValue: ValidPropertyValue,
  ): void {
    this.dispatchEvent(
      new CustomEvent<PropertyModificationEvent>(modificationType, {
        detail: {
          propertyName,
          propertyValue,
        },
      }),
    );
  }

  trackEvent(eventName: TrackedEvents, properties: Partial<TrackedProperties>): void {
    emitTrackEvent(this, {
      eventName,
      props: properties,
    });
  }
}

export const ProfileEditablePropertyEl = defineAndMakeJsxFactory(`profile-editable-property`, ProfileEditableProperty);
export const ProfileEditablePropertyReact = panel2react(`profile-editable-property`, ProfileEditableProperty);
